import React, { useCallback, useContext, useMemo } from "react";
import { HeaderA } from "components/headers";
import lang from "translations";
import { Path } from "paths";
import { VenueContext } from "contexts";
import { useApi, useMount, useFilter } from "hooks";
import { searchResource } from "apis";
import { ButtonLink, DataTable, Pill } from "components/commons";
import { PillType } from "enums";
import { prettifyVoucherStatus } from "services";
import { resource } from "mappers";
import { ModuleWrapper } from "components/fragments";
import columns from "./columns";

const ResourceList = () => {
  const { venue } = useContext(VenueContext);
  const { venueId } = venue;

  const {
    request,
    loading = true,
    error,
    result = { data: [] },
    mappedData,
  } = useApi({
    api: searchResource,
    isArray: true,
    mapper: resource,
    returnMappedData: true,
  });

  const defaultFilter = {
    venueId,
    page: 1,
    pageSize: 20,
    searchKey: "",
  };
  const { modifyFilters, filterState } = useFilter(defaultFilter, defaultFilter);

  useMount(() => {
    request(filterState);
  });

  const fetchUpdateStore = useCallback(
    async (requestState) => {
      request(requestState);
    },
    [request]
  );

  const renderData = useMemo(() => {
    return mappedData.map(
      ({
        resourceId,
        defaultName,
        customName,
        capacity,
        resourceStatusName,
        resourceTypeName,
        locationName,
        zoneName,
        allowOnline,
        allowWalkIn,
      }) => {
        return {
          defaultName: (
            <ButtonLink path={Path.RESOURCE_ID(resourceId)}>
              {defaultName}
            </ButtonLink>
          ),
          customName: customName ?? "-",
          capacity: capacity ?? "-",
          resourceStatusName: (
            <Pill
              type={resourceStatusName.toUpperCase() === "ACTIVE" ? PillType.Green : resourceStatusName.toUpperCase() === "INACTIVE" ? PillType.Opaque : PillType.Gray}
              size="text-xs"
            >
              {prettifyVoucherStatus(resourceStatusName.toUpperCase())}
            </Pill>
          ),
          resourceTypeName: resourceTypeName ?? "-",
          locationName: locationName ?? "-",
          zoneName: zoneName ?? "-",
          allowOnline: allowOnline ? lang.yes : lang.no,
          allowWalkIn: allowWalkIn ? lang.yes : lang.no,
        };
      }
    );
  }, [mappedData]);

  return (
    <ModuleWrapper>
      <HeaderA
        title={lang.resources}
        description={lang.manageResources}
        button={{
          path: Path.RESOURCE_CREATE,
          text: lang.createNewResource,
        }}
        className="mb-md"
      />
      <text>{result.data?.total} {lang.searchResults}</text>
      <DataTable
        page={filterState.page}
        pageSize={filterState.pageSize}
        onChangePage={modifyFilters}
        fetchList={fetchUpdateStore}
        total={result.data?.total}
        renderEmpty={{
          image: "https://pouch-inventory.s3.us-east-2.amazonaws.com/landing-image/no-resource-s.png",
          title: lang.createResourceToManage,
        }}
        loading={loading}
        columns={columns}
        data={renderData}
        error={error}
      />
    </ModuleWrapper>
  );
};

export default ResourceList;
